import { getScopedDomain } from '../lib/tenantScope';
// Hotel Lite — Room Status board (#1052).
// One row per extension ("room"): current housekeeping status (set by dialing
// *68<MAID code> from the room phone, or from the select here) plus the next
// pending wake-up call. Pairs with the Wake-up Calls page.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BedDouble, BellRing, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  vacant_dirty: 'Vacant — Dirty',
  vacant_clean: 'Vacant — Clean',
  vacant_inspected: 'Vacant — Inspected',
  occupied_dirty: 'Occupied — Dirty',
  occupied_clean: 'Occupied — Clean',
  do_not_disturb: 'Do Not Disturb',
  service_refused: 'Service Refused',
  out_of_service: 'Out of Service',
  out_of_order: 'Out of Order',
  maintenance_request: 'Maintenance Requested',
};

function statusBadgeClass(status?: string | null) {
  if (!status) return 'badge bg-textSecondary/20';
  if (status.endsWith('_clean') || status.endsWith('_inspected')) return 'badge bg-success/20 text-success';
  if (status.endsWith('_dirty') || status === 'maintenance_request') return 'badge bg-danger/20 text-danger';
  if (status === 'do_not_disturb') return 'badge bg-primary/20 text-primary';
  return 'badge bg-textSecondary/20'; // service_refused / out_of_service / out_of_order
}

function describeSchedule(s: any) {
  if (!s) return null;
  if (s.one_time) return `Once: ${new Date(s.one_time).toLocaleString()}`;
  if (s.recurring) return `${(s.recurring.days || []).join(',')} @ ${s.recurring.time}`;
  return null;
}

export default function RoomStatus() {
  const me = api.me();
  const qc = useQueryClient();
  const toast = useToast();
  const dom = getScopedDomain(me?.domain_uuid);

  const { data: rooms = [], isFetching, refetch } = useQuery({
    queryKey: ['room-status', dom],
    queryFn: () => dom ? api.roomStatus.board(dom) : Promise.resolve([]),
    enabled: !!dom,
    refetchInterval: 30_000, // housekeeping sets codes from room phones — keep the board live
  });

  const { data: codes = {} } = useQuery({
    queryKey: ['room-status-codes'],
    queryFn: () => api.roomStatus.codes(),
  });

  const setStatus = useMutation({
    mutationFn: ({ extension_uuid, status_code }: { extension_uuid: string; status_code: string }) =>
      api.roomStatus.set(extension_uuid, { domain_uuid: dom!, status_code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['room-status', dom] }),
    onError: (e: any) => toast('error', e?.message || 'Could not update room status'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><BedDouble size={22} /> Room Status</h1>
        <div className="flex items-center gap-2">
          <button className="btn-ghost p-2" title="Refresh" onClick={() => refetch()}>
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          </button>
          <Link to="/wakeup-calls" className="btn"><BellRing size={14} className="inline mr-1" /> Wake-up Calls</Link>
        </div>
      </div>

      <p className="text-sm text-textSecondary">
        Housekeeping dials <span className="font-mono">*68</span> + status code from the room phone
        (e.g. <span className="font-mono">*6801</span> = Vacant — Clean, <span className="font-mono">*6812</span> = Do Not Disturb),
        or set the status here from the front desk.
      </p>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Room / Ext</th><th>Name</th><th>Housekeeping</th><th>Set status</th><th>Updated</th><th>Next wake-up</th></tr></thead>
          <tbody>
            {rooms.map((r: any) => (
              <tr key={r.extension_uuid} className="hover:bg-elevated">
                <td className="font-mono">{r.extension}</td>
                <td className="text-sm">{r.name || '—'}</td>
                <td>
                  <span className={statusBadgeClass(r.status)}>
                    {r.status ? (STATUS_LABELS[r.status] || r.status) : 'Not set'}
                  </span>
                  {r.reported_by === 'phone' && r.status && <span className="text-xs text-textSecondary ml-1">(*68)</span>}
                </td>
                <td>
                  <select
                    className="input py-1 text-sm"
                    value={r.status_code || ''}
                    onChange={e => e.target.value && setStatus.mutate({ extension_uuid: r.extension_uuid, status_code: e.target.value })}
                  >
                    <option value="" disabled>Set…</option>
                    {Object.entries(codes).map(([code, slug]) => (
                      <option key={code} value={code}>{code} — {STATUS_LABELS[slug as string] || slug}</option>
                    ))}
                  </select>
                </td>
                <td className="text-xs text-textSecondary">{r.status_updated_at ? new Date(r.status_updated_at).toLocaleString() : '—'}</td>
                <td className="text-sm">
                  {describeSchedule(r.next_wakeup_schedule) || <span className="text-textSecondary">—</span>}
                </td>
              </tr>
            ))}
            {rooms.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">No extensions in this department.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
